import React, { useState, useEffect } from 'react';
import { applyTimeZone } from '../../../utils/utils';
import { downloadImage } from '../../../utils/upload-image';
import { PLACEHOLDER_PROFILE_NO_PHOTO } from '../../../utils/placeholders';
import { getCastellerFullName } from '../../../utils/castellerName';
import { requiresStrongDeletionConfirmation } from './deletionProtection';
import DeletionActivitySummary from './DeletionActivitySummary';

function DeletionConfirmModal({
    user,
    deletionSummary,
    loadingSummary,
    onConfirm,
    onCancel,
    COLLA
}) {
    const [profilePic, setProfilePic] = useState(null);
    const [confirmedReferencedDeletion, setConfirmedReferencedDeletion] = useState(false);

    useEffect(() => {
        if (COLLA) {
            downloadImage(user.id, COLLA)
                .then(base64 => setProfilePic(base64))
                .catch(() => setProfilePic(false));
        }
    }, [user.id, COLLA]);
    const isActive = requiresStrongDeletionConfirmation(deletionSummary);
    const deleteDisabled = loadingSummary || (isActive && !confirmedReferencedDeletion);

    return (
        <div className="actions-modal-overlay" onClick={onCancel}>
            <div
                className="actions-modal-content"
                onClick={(e) => e.stopPropagation()}
                style={{ minWidth: '420px', maxWidth: '90%' }}
            >
                <button className="close-button" onClick={onCancel}>✕</button>

                <div className="actions-modal-header">
                    <h3>Confirmar eliminació</h3>
                </div>

                <div style={{ padding: '24px' }}>
                    {/* User Info Card */}
                    <p style={{
                        margin: '0 0 12px',
                        fontSize: '15px',
                        color: '#718096',
                        fontWeight: '500'
                    }}>
                        Estàs a punt d'eliminar:
                    </p>

                    <div style={{
                        background: '#f7fafc',
                        padding: '16px',
                        borderRadius: '12px',
                        marginBottom: '24px',
                        border: '1px solid #e2e8f0',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '16px'
                    }}>
                        {/* Profile Photo */}
                        <div style={{
                            width: '56px',
                            height: '56px',
                            borderRadius: '10px',
                            overflow: 'hidden',
                            flexShrink: 0,
                            border: '1px solid #e2e8f0',
                            background: '#fff'
                        }}>
                            {profilePic === null ? (
                                <div style={{
                                    width: '100%',
                                    height: '100%',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    color: '#a0aec0',
                                    fontSize: '12px'
                                }}>
                                    ...
                                </div>
                            ) : (
                                <img
                                    src={profilePic || PLACEHOLDER_PROFILE_NO_PHOTO}
                                    alt={getCastellerFullName(user, 'Usuari')}
                                    style={{
                                        width: '100%',
                                        height: '100%',
                                        objectFit: 'cover'
                                    }}
                                />
                            )}
                        </div>
                        {/* User Info */}
                        <div style={{ flex: 1 }}>
                            <strong style={{
                                fontSize: '16px',
                                color: '#2d3748'
                            }}>
                                {getCastellerFullName(user, 'Usuari', { includeSecondSurname: true })}
                            </strong>
                            {user.mote ? (
                                <div style={{
                                    fontSize: '14px',
                                    color: '#718096',
                                    marginTop: '4px'
                                }}>
                                    "{user.mote}"
                                </div>
                            ) : null}
                            {deletionSummary?.createdAt ? (
                                <div style={{
                                    fontSize: '13px',
                                    color: '#a0aec0',
                                    marginTop: '8px',
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '6px'
                                }}>
                                    <span>📅</span>
                                    {applyTimeZone(deletionSummary.createdAt).isBefore('2026-02-03') ? (
                                        <span>Creat abans del 2026</span>
                                    ) : (
                                        <span>Creat el {applyTimeZone(deletionSummary.createdAt).format('D [de] MMMM [de] YYYY')}</span>
                                    )}
                                </div>
                            ) : null}
                        </div>
                    </div>

                    {/* Loading State */}
                    {loadingSummary ? (
                        <div style={{
                            textAlign: 'center',
                            padding: '32px 20px',
                            color: '#718096',
                            fontSize: '14px'
                        }}>
                            <div style={{ marginBottom: '12px', fontSize: '24px' }}>⏳</div>
                            Carregant activitat...
                        </div>
                    ) : deletionSummary?.error ? (
                        /* Error State */
                        <div style={{
                            background: '#fff3cd',
                            border: '1px solid #ffc107',
                            borderRadius: '12px',
                            padding: '16px',
                            marginBottom: '20px',
                            fontSize: '14px',
                            color: '#856404'
                        }}>
                            <strong>⚠️ Advertiment:</strong> No s'ha pogut carregar el resum d'activitat.
                            Procedeix amb precaució.
                        </div>
                    ) : deletionSummary ? (
                        <DeletionActivitySummary
                            summary={deletionSummary}
                            requiresConfirmation={isActive}
                            confirmed={confirmedReferencedDeletion}
                            onConfirmedChange={setConfirmedReferencedDeletion}
                        />
                    ) : null}

                    {/* Action Buttons */}
                    <div style={{
                        display: 'flex',
                        gap: '12px',
                        marginTop: '24px'
                    }}>
                        <button
                            onClick={onCancel}
                            style={{
                                flex: 1,
                                padding: '14px',
                                border: '1px solid #e2e8f0',
                                background: 'white',
                                borderRadius: '10px',
                                fontSize: '15px',
                                fontWeight: '600',
                                cursor: 'pointer',
                                color: '#4a5568',
                                transition: 'all 0.2s ease'
                            }}
                            onMouseOver={(e) => {
                                e.target.style.background = '#f7fafc';
                                e.target.style.borderColor = '#cbd5e0';
                            }}
                            onMouseOut={(e) => {
                                e.target.style.background = 'white';
                                e.target.style.borderColor = '#e2e8f0';
                            }}
                        >
                            Cancel·lar
                        </button>
                        <button
                            onClick={onConfirm}
                            disabled={deleteDisabled}
                            style={{
                                flex: 1,
                                padding: '14px',
                                border: 'none',
                                background: deleteDisabled ? '#cbd5e0' : '#e53e3e',
                                borderRadius: '10px',
                                fontSize: '15px',
                                fontWeight: '600',
                                cursor: deleteDisabled ? 'not-allowed' : 'pointer',
                                color: 'white',
                                transition: 'all 0.2s ease'
                            }}
                            onMouseOver={(e) => {
                                if (!deleteDisabled) {
                                    e.target.style.background = '#c53030';
                                    e.target.style.transform = 'translateY(-1px)';
                                }
                            }}
                            onMouseOut={(e) => {
                                if (!deleteDisabled) {
                                    e.target.style.background = '#e53e3e';
                                    e.target.style.transform = 'translateY(0)';
                                }
                            }}
                        >
                            {loadingSummary
                                ? 'Carregant...'
                                : isActive && !confirmedReferencedDeletion
                                    ? 'Confirma les referències'
                                    : 'Eliminar usuari'}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
}

export default DeletionConfirmModal;
